import { MME_API_URL } from "@/providers/mme/env";

// Cookie-free preview GETs otherwise receive ngrok's interstitial as a blob.
const previewTunnelHeaders: Record<string, string> = new URL(MME_API_URL)
  .hostname.endsWith(".ngrok-free.app")
  ? { "ngrok-skip-browser-warning": "1" }
  : {};

export type SharedProfile = {
  first_name: string;
  age: number | null;
  city: string | null;
  biography: string | null;
  occupation: string | null;
  interests: string[];
  prompts: { question: string; answer: string }[];
  photos: { index: number }[];
  details?: { label: string; value: string }[];
  onboarding?: {
    key: string;
    title: string;
    items: { question: string; answer: string | null }[];
  }[];
  preferences?: { label: string; value: string }[];
};
export type SharePreview = { profile: SharedProfile; preview_digest: string };
export type ProfileShare = {
  id: number;
  label: string;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
};
export const shareUrl = (token: string) =>
  `${location.origin}${location.pathname}#/shared-profile/${token}`;

/** Consume once before importing any application code; never persist the secret. */
export function consumeShareToken(): string {
  const token =
    location.hash.match(/^#\/shared-profile\/([A-Za-z0-9_-]{43})$/)?.[1] ?? "";
  history.replaceState(null, "", `${location.pathname}#/shared-profile`);
  return token;
}

export async function readSharedProfile(token: string, signal?: AbortSignal) {
  const response = await fetch(`${MME_API_URL}/profile-shares/read`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
    cache: "no-store",
    credentials: "omit",
    referrerPolicy: "no-referrer",
    signal,
  });
  if (!response.ok) throw new Error("This profile link is unavailable.");
  return response.json() as Promise<{
    profile: SharedProfile;
    expires_at: string;
  }>;
}

export async function sharePhoto(
  access:
    | { token: string }
    | { memberId: number; digest: string; bearer: string },
  index: number,
  signal: AbortSignal,
): Promise<string> {
  const recipient = "token" in access;
  const response = await fetch(
    `${MME_API_URL}${
      recipient
        ? "/profile-shares/photo"
        : `/staff/members/${access.memberId}/profile-shares/preview/photos/${index}?preview_digest=${encodeURIComponent(access.digest)}`
    }`,
    {
      method: recipient ? "POST" : "GET",
      headers: recipient
        ? { "Content-Type": "application/json" }
        : { Authorization: `Bearer ${access.bearer}`, ...previewTunnelHeaders },
      body: recipient
        ? JSON.stringify({ token: access.token, index })
        : undefined,
      cache: "no-store",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal,
    },
  );
  if (!response.ok) throw new Error("Photo unavailable");
  return URL.createObjectURL(await response.blob());
}

export async function sharePdf(
  access: Parameters<typeof sharePhoto>[0],
  signal: AbortSignal,
): Promise<Blob> {
  const recipient = "token" in access;
  const response = await fetch(
    `${MME_API_URL}${recipient ? "/profile-shares/pdf" : `/staff/members/${access.memberId}/profile-shares/preview/pdf?preview_digest=${encodeURIComponent(access.digest)}`}`,
    {
      method: recipient ? "POST" : "GET",
      headers: recipient
        ? { "Content-Type": "application/json" }
        : { Authorization: `Bearer ${access.bearer}`, ...previewTunnelHeaders },
      body: recipient ? JSON.stringify({ token: access.token }) : undefined,
      cache: "no-store",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal,
    },
  );
  if (response.status === 409)
    throw new Error(
      "The profile changed. Close and reopen the preview before downloading.",
    );
  if (!response.ok)
    throw new Error(
      "This PDF is unavailable. Refresh the profile and try again.",
    );
  return response.blob();
}
